import { NpsSurveyAnswerValue } from '@nicejob-library/enums';
import { INamespaceTranslationKeyMap } from '@nicejob-library/internationalization/types';

/**
 * It is separated from DefaultNpsSurveyQuestionId because
 * in future we might want to support custom questions with UUID
 * as IDs.
 */
export enum NpsSurveyQuestionTranslationKey {
    ServicePerformance = 'service_performance',
    Pricing = 'pricing',
    SchedulingCommunication = 'scheduling_communication',
    Professionalism = 'professionalism',
    Retention = 'retention',
}

/**
 * Default NPS survey question IDs
 * These are the unique identifiers for each default question.
 * Currently they match the translation keys, but can be UUIDs in the future.
 *
 * @note The question ID is the value of the enum.
 * @note The question ID must be unique and never exist before.
 * Please check database to ensure.
 */
export enum DefaultNpsSurveyQuestionId {
    ServicePerformance = 'service_performance',
    Pricing = 'pricing',
    SchedulingCommunication = 'scheduling_communication',
    Professionalism = 'professionalism',
    Retention = 'retention',
}

/**
 * i18n label keys (insights namespace) for each default question.
 * Used by formatNpsCampaignQuestionBreakdown to translate question labels.
 *
 * @note Update DEFAULT_NPS_SURVEY_QUESTIONS as well
 */
export const NPS_SURVEY_QUESTION_LABEL_KEYS: Record<
    string,
    INamespaceTranslationKeyMap<'insights'>
> = {
    [DefaultNpsSurveyQuestionId.ServicePerformance]:
        'feedback_routing.survey_insights.question.service_performance',
    [DefaultNpsSurveyQuestionId.Pricing]: 'feedback_routing.survey_insights.question.pricing',
    [DefaultNpsSurveyQuestionId.SchedulingCommunication]:
        'feedback_routing.survey_insights.question.scheduling_communication',
    [DefaultNpsSurveyQuestionId.Professionalism]:
        'feedback_routing.survey_insights.question.professionalism',
    [DefaultNpsSurveyQuestionId.Retention]: 'feedback_routing.survey_insights.question.retention',
};

/**
 * NPS score categories.
 * Used in notifications and email templates to classify respondents.
 */
export enum NpsCategory {
    Promoter = 'Promoter',
    Passive = 'Passive',
    Detractor = 'Detractor',
}

/**
 * Maps NpsSurveyAnswerValue enum values to numeric scores (1–5).
 */
export const NPS_ANSWER_TO_SCORE: Record<NpsSurveyAnswerValue, number> = {
    [NpsSurveyAnswerValue.StronglyDisagree]: 1,
    [NpsSurveyAnswerValue.Disagree]: 2,
    [NpsSurveyAnswerValue.Neutral]: 3,
    [NpsSurveyAnswerValue.Agree]: 4,
    [NpsSurveyAnswerValue.StronglyAgree]: 5,
};

/**
 * Default NPS survey question definition (without translated text)
 */
export interface INpsSurveyQuestionDefinition {
    default_survey_question_id: DefaultNpsSurveyQuestionId;
    translation_key: NpsSurveyQuestionTranslationKey;
    required: boolean;
}

/**
 * Classifies a raw NPS score (0-10) into its category.
 * Promoter >= 9, Passive 7-8, Detractor <= 6.
 */
export function getNpsCategory(score: number): NpsCategory {
    if (score >= 9) {
        return NpsCategory.Promoter;
    }
    if (score >= 7) {
        return NpsCategory.Passive;
    }

    return NpsCategory.Detractor;
}

/**
 * Default NPS survey questions configuration
 */
export const DEFAULT_NPS_SURVEY_QUESTIONS: INpsSurveyQuestionDefinition[] = [
    {
        default_survey_question_id: DefaultNpsSurveyQuestionId.ServicePerformance,
        translation_key: NpsSurveyQuestionTranslationKey.ServicePerformance,
        required: false,
    },
    {
        default_survey_question_id: DefaultNpsSurveyQuestionId.Pricing,
        translation_key: NpsSurveyQuestionTranslationKey.Pricing,
        required: false,
    },
    {
        default_survey_question_id: DefaultNpsSurveyQuestionId.SchedulingCommunication,
        translation_key: NpsSurveyQuestionTranslationKey.SchedulingCommunication,
        required: false,
    },
    {
        default_survey_question_id: DefaultNpsSurveyQuestionId.Professionalism,
        translation_key: NpsSurveyQuestionTranslationKey.Professionalism,
        required: false,
    },
    {
        default_survey_question_id: DefaultNpsSurveyQuestionId.Retention,
        translation_key: NpsSurveyQuestionTranslationKey.Retention,
        required: false,
    },
];

/**
 * Default page size for the NPS responses feed (Insights → NPS → Responses tab
 * and the Person activity feed). Shared between the GraphQL resolver
 * (`feed-pagination.clampLimit`) and the frontend hook (`useNpsFeedQuery`) so
 * both sides agree on a single number — bumping it here updates both at once.
 */
export const NPS_FEED_DEFAULT_LIMIT = 20;
